import { BArtistCard } from './BArtistCard';
import './BDiscoverSection.css';

interface KeyArtistLinks {
  bandcamp?: string;
  spotify?: string;
  youtube?: string;
  youtubeVideoId?: string;
}

interface GenrePanel {
  description?: string;
  keyArtists?: Array<{
    name: string;
    track?: string;
    significance?: string;
    links?: KeyArtistLinks;
  }>;
}

interface Artist {
  name: string;
  track?: string;
  tracks?: string[];
  description?: string;
  matchReason?: string;
  location?: string | null;
  area?: string;
  youtubeVideoId?: string;
  bandcampEmbed?: {
    embedHtml: string;
    albumId?: string;
    title?: string;
    bandcampUrl?: string;
  };
  links?: Record<string, string | undefined>;
  members?: Array<{ name: string; role?: string }>;
}

interface BDiscoverSectionProps {
  artists: Artist[];
  genrePanel?: GenrePanel;
  isBuilding?: boolean;
  onArtistClick?: (artistName: string) => void;
}

/**
 * Discover section with featured artist and grid.
 */
export function BDiscoverSection({
  artists,
  genrePanel,
  isBuilding = false,
  onArtistClick,
}: BDiscoverSectionProps) {
  // Dedupe key artists by name (case-insensitive)
  const uniqueKeyArtists = (genrePanel?.keyArtists || []).filter((artist, index, arr) => {
    const firstIndex = arr.findIndex(a => a.name.toLowerCase() === artist.name.toLowerCase());
    return firstIndex === index;
  });

  // Build set of key artist names (lowercase) for deduplication against discovered
  const keyArtistNames = new Set(
    uniqueKeyArtists.map(a => a.name.toLowerCase())
  );

  // Filter out discovered artists that are already in the genre panel
  const uniqueDiscoveredArtists = artists.filter(
    a => !keyArtistNames.has(a.name.toLowerCase())
  );

  // First artist is featured, rest go in grid
  const [featuredArtist, ...otherArtists] = uniqueDiscoveredArtists;
  const gridArtists = otherArtists.slice(0, 5); // Show up to 5 more for 6 total

  return (
    <section className="b-discover-section">
      <div className="b-discover-section__header">
        <div>
          <span className="b-section-number">01</span>
          <h2 className="b-section-title">
            Discover
            {isBuilding && <span className="b-building-dot" />}
          </h2>
        </div>
        <span className="b-discover-section__note">Artists you need to hear</span>
      </div>

      {/* Genre panel with description and key artists - same layout as discover */}
      {genrePanel && (genrePanel.description || uniqueKeyArtists.length > 0) && (
        <div className="b-discover-section__genre-panel">
          {genrePanel.description && (
            <p className="b-discover-section__genre-desc">{genrePanel.description}</p>
          )}
          {uniqueKeyArtists.length > 0 && (
            <div className="b-discover-grid">
              {/* First key artist is featured (spans 2 columns) */}
              {uniqueKeyArtists[0] && (
                <BArtistCard
                  key={uniqueKeyArtists[0].name}
                  artist={{
                    name: uniqueKeyArtists[0].name,
                    track: uniqueKeyArtists[0].track,
                    description: uniqueKeyArtists[0].significance,
                    youtubeVideoId: uniqueKeyArtists[0].links?.youtubeVideoId,
                    links: uniqueKeyArtists[0].links ? { ...uniqueKeyArtists[0].links } : undefined,
                  }}
                  isFeatured
                  onArtistClick={onArtistClick}
                />
              )}
              {/* Rest go in 2-column grid below */}
              {uniqueKeyArtists.slice(1).map((keyArtist) => (
                <BArtistCard
                  key={keyArtist.name}
                  artist={{
                    name: keyArtist.name,
                    track: keyArtist.track,
                    description: keyArtist.significance,
                    youtubeVideoId: keyArtist.links?.youtubeVideoId,
                    links: keyArtist.links ? { ...keyArtist.links } : undefined,
                  }}
                  onArtistClick={onArtistClick}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Artist grid - wrapped in panel like genre panel */}
      <div className="b-discover-section__discover-panel">
        <div className="b-discover-grid">
          {featuredArtist && (
            <BArtistCard
              artist={featuredArtist}
              number={1}
              isFeatured
              onArtistClick={onArtistClick}
            />
          )}

          {gridArtists.map((artist, index) => (
            <BArtistCard
              key={artist.name}
              artist={artist}
              number={index + 2}
              onArtistClick={onArtistClick}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
